import {
  createContext,
  createEffect,
  createResource,
  createSignal,
  onCleanup,
  useContext,
  type Accessor,
  type JSX,
  type Resource,
} from "solid-js";
import { createStore, reconcile } from "solid-js/store";
import { detectCapabilities, type Capabilities } from "@/core/capability/index.ts";
import {
  createFeatureRegistry,
  type FeatureId,
  type FeatureRegistry,
  type PackState,
} from "@/core/features/index.ts";
import {
  createProjectRepository,
  openOpfsStore,
  openProjectDatabase,
  readPreferences,
  writePreferences,
  type BlobStore,
  type Preferences,
  type ProjectRepository,
} from "@/core/storage/index.ts";
import { FEATURE_PACKS } from "@/engines/packs.ts";

export type Storage = {
  readonly blobs: BlobStore;
  readonly projects: ProjectRepository;
};

export type AppServices = {
  readonly capabilities: Capabilities;
  readonly features: FeatureRegistry;
  /** Reactive view of a pack's download state. */
  readonly featureState: (id: FeatureId) => PackState;
  readonly preferences: Accessor<Preferences>;
  readonly updatePreferences: (patch: Partial<Preferences>) => void;
  readonly storage: Resource<Storage>;
};

const AppContext = createContext<AppServices>();

async function openStorage(): Promise<Storage> {
  const [blobs, database] = await Promise.all([openOpfsStore(), openProjectDatabase()]);
  return { blobs, projects: createProjectRepository(database, blobs) };
}

export function AppProvider(props: { children: JSX.Element }): JSX.Element {
  const capabilities = detectCapabilities();
  const features = createFeatureRegistry(FEATURE_PACKS, capabilities);

  const [packStates, setPackStates] = createStore<Record<string, PackState>>(
    Object.fromEntries(features.states()),
  );
  onCleanup(features.subscribe((id, state) => setPackStates(id, reconcile(state))));

  const [preferences, setPreferences] = createSignal(readPreferences());
  const updatePreferences = (patch: Partial<Preferences>): void => {
    const next = { ...preferences(), ...patch };
    setPreferences(next);
    writePreferences(next);
  };

  const [storage] = createResource(openStorage);

  /**
   * soluid only defines a dark palette under `[data-theme="dark"]`, so "自動" is resolved
   * here and kept in step with the OS. That keeps one definition of the palette rather
   * than a second copy inside a `prefers-color-scheme` block.
   */
  const prefersDark =
    typeof matchMedia === "function" ? matchMedia("(prefers-color-scheme: dark)") : null;
  const [systemDark, setSystemDark] = createSignal(prefersDark?.matches ?? false);
  if (prefersDark) {
    const follow = (event: MediaQueryListEvent): void => {
      setSystemDark(event.matches);
    };
    prefersDark.addEventListener("change", follow);
    onCleanup(() => prefersDark.removeEventListener("change", follow));
  }

  createEffect(() => {
    const { theme } = preferences();
    const resolved = theme === "system" ? (systemDark() ? "dark" : "light") : theme;
    document.documentElement.setAttribute("data-theme", resolved);
  });

  const services: AppServices = {
    capabilities,
    features,
    featureState: (id) =>
      packStates[id] ?? { status: "unsupported", reason: "未登録のモジュールです" },
    preferences,
    updatePreferences,
    storage,
  };

  return <AppContext.Provider value={services}>{props.children}</AppContext.Provider>;
}

export function useApp(): AppServices {
  const services = useContext(AppContext);
  if (!services) throw new Error("AppProvider の外で useApp が呼ばれました");
  return services;
}
